import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';

import {
  RESET_GRAPH_CONSISTENCY_FORECAST,
  RESET_GRAPH_SWELL_SPECTRA_FORECAST,
  RESET_GRAPH_WEATHER_FORECAST,
  fetchGraphConditionForecast,
  fetchGraphConsistencyForecast,
  fetchGraphRatingForecast,
  fetchGraphSunlightTimes,
  fetchGraphSurfForecast,
  fetchGraphLiveSurfForecast,
  fetchGraphSwellsForecast,
  fetchGraphEnergyForecast,
  fetchGraphSwellSpectraForecast,
  fetchGraphTideForecast,
  fetchGraphWeatherForecast,
  fetchGraphWindForecast,
} from 'actions/graphs';
import useFetchLiveWind from 'components/LiveWindModal/hooks/useFetchLiveWind';
import { useGraphData } from 'selectors/graphs';
import { useAppDispatch } from 'stores/hooks';
import type { Units } from 'types/units';
import { checkStartDateIsValid } from './useUpdateHistoricForecastURL';
// Helper function to clear router query params when navigating between spots
const clearedRouterQuery = (obj: Record<string, unknown>) => {
  const entries = Object.entries(obj);
  const undefinedEntries = entries.map(([key, value]) => {
    if (key === 'spotId' || key === 'spotSlug') {
      return [key, value];
    }
    return [key, undefined];
  });
  return Object.fromEntries(undefinedEntries);
};

export interface FetchGraphDataProps {
  consistencyEnabled: boolean;
  hasLiveWind: boolean;
  hasHourlyDataAccess?: boolean;
  isHistoricView?: boolean;
  isPremium: boolean;
  supportsInsights: boolean;
  maxDaysToFetch: number;
  numDaysToFetch: number;
  spectraDataAvailable: boolean;
  spectraGraphOpen: boolean;
  spotId: string;
  startDate?: string | undefined;
  subregionId: string;
  units: Units;
}

const useFetchGraphData = ({
  consistencyEnabled,
  hasLiveWind,
  hasHourlyDataAccess,
  isHistoricView,
  isPremium,
  supportsInsights,
  maxDaysToFetch,
  numDaysToFetch,
  spectraDataAvailable,
  spectraGraphOpen,
  spotId,
  startDate,
  subregionId,
  units,
}: FetchGraphDataProps) => {
  const shouldFetchHourly = hasHourlyDataAccess ?? isPremium;

  const INTERVAL_CONDITION = 1;
  const INTERVAL_RATING = 1;
  const INTERVAL_TIDE = 1;
  const INTERVAL_WAVE = 1;
  const INTERVAL_WAVE_COUNT = 1;
  const INTERVAL_WEATHER = 1;
  const INTERVAL_SUNLIGHT = 1;
  const INTERVAL_WIND = 1;

  const INTERVAL_RATING_16DAY = shouldFetchHourly ? 1 : 3;
  const INTERVAL_WAVE_16DAY = shouldFetchHourly ? 1 : 3;
  const INTERVAL_WAVE_COUNT_16DAY = shouldFetchHourly ? 1 : 3;
  const INTERVAL_WEATHER_16DAY = shouldFetchHourly ? 1 : 3;
  const INTERVAL_SUNLIGHT_16DAY = 3;
  const INTERVAL_WIND_16DAY = shouldFetchHourly ? 1 : 3;
  const WEATHER_DAYS_TO_FETCH = 16;

  const validStartDate = checkStartDateIsValid(startDate) && isPremium ? startDate : undefined;
  const dispatch = useAppDispatch();
  const router = useRouter();

  const clearedQueryParams = clearedRouterQuery(router.query);

  const searchParams = spotId === router.query?.spotId ? router?.query : clearedQueryParams;

  const graphData = useGraphData();

  const shouldFetchConsistencyData = supportsInsights || consistencyEnabled;

  const {
    data: liveWindData,
    loading: liveWindDataLoading,
    error: liveWindDataError,
  } = useFetchLiveWind(spotId, !isPremium || !hasLiveWind || isHistoricView);

  const shouldFetchSwellSpectraData = isPremium && spectraDataAvailable;

  const fetchSwellSpectraData = useCallback(() => {
    if (shouldFetchSwellSpectraData) {
      dispatch(
        fetchGraphSwellSpectraForecast({
          id: spotId,
          days: numDaysToFetch,
          intervalHours: INTERVAL_WAVE_COUNT,
          desired16DayInterval: INTERVAL_WAVE_COUNT_16DAY,
          isGraphUpdates: false,
          query: searchParams,
          startDate: validStartDate,
        }),
      );
    }
  }, [
    INTERVAL_WAVE_COUNT_16DAY,
    dispatch,
    numDaysToFetch,
    shouldFetchSwellSpectraData,
    spotId,
    searchParams,
    validStartDate,
  ]);

  const fetchAllGraphData = useCallback(
    (skipSwellSpectraFetch?: boolean) => {
      dispatch(
        fetchGraphConditionForecast({
          id: subregionId,
          days: numDaysToFetch,
          intervalHours: INTERVAL_CONDITION,
          isPremium,
          query: searchParams,
          startDate: validStartDate,
        }),
      );

      dispatch(
        fetchGraphRatingForecast({
          days: numDaysToFetch,
          desired16DayInterval: INTERVAL_RATING_16DAY,
          id: spotId,
          intervalHours: INTERVAL_RATING,
          isPremium: isPremium || maxDaysToFetch > 2,
          query: searchParams,
          startDate: validStartDate,
        }),
      );

      dispatch(
        fetchGraphSurfForecast()({
          id: spotId,
          days: numDaysToFetch,
          intervalHours: INTERVAL_WAVE,
          desired16DayInterval: INTERVAL_WAVE_16DAY,
          isGraphUpdates: true,
          query: searchParams,
          isPremium,
          startDate: validStartDate,
          units,
        }),
      );

      if (!isHistoricView) {
        dispatch(
          fetchGraphLiveSurfForecast()({
            id: spotId,
            days: numDaysToFetch,
            intervalHours: INTERVAL_WAVE,
            query: searchParams,
            isPremium: shouldFetchHourly,
            units,
          }),
        );
      }

      dispatch(
        fetchGraphSwellsForecast()({
          id: spotId,
          days: numDaysToFetch,
          intervalHours: INTERVAL_WAVE,
          desired16DayInterval: INTERVAL_WAVE_16DAY,
          isGraphUpdates: true,
          query: searchParams,
          isPremium,
          startDate: validStartDate,
          units,
        }),
      );

      dispatch(
        fetchGraphEnergyForecast()({
          id: spotId,
          days: numDaysToFetch,
          intervalHours: INTERVAL_WAVE,
          desired16DayInterval: INTERVAL_WAVE_16DAY,
          isGraphUpdates: true,
          query: { ...searchParams, corrected: isPremium },
          isPremium,
          startDate: validStartDate,
          units,
        }),
      );

      dispatch(
        fetchGraphWindForecast({
          id: spotId,
          days: maxDaysToFetch,
          intervalHours: INTERVAL_WIND,
          desired16DayInterval: INTERVAL_WIND_16DAY,
          isGraphUpdates: false,
          query: {
            ...router.query,
            correctedWind: isPremium,
          },
          startDate: validStartDate,
          isPremium,
          units,
        }),
      );

      dispatch(
        fetchGraphSunlightTimes({
          id: spotId,
          days: WEATHER_DAYS_TO_FETCH,
          intervalHours: INTERVAL_SUNLIGHT,
          desired16DayInterval: INTERVAL_SUNLIGHT_16DAY,
          query: searchParams,
          startDate: validStartDate,
        }),
      );

      dispatch(
        fetchGraphTideForecast({
          id: spotId,
          // Note: we need to fetch an extra day to be able to interpolate
          // the tide data points between the last tide data point and midnight
          days: numDaysToFetch + 1,
          intervalHours: INTERVAL_TIDE,
          isGraphUpdates: false,
          query: searchParams,
          isPremium,
          startDate: validStartDate,
          units,
        }),
      );

      if (!isHistoricView && !validStartDate) {
        dispatch(
          fetchGraphWeatherForecast({
            id: spotId,
            days: WEATHER_DAYS_TO_FETCH,
            intervalHours: INTERVAL_WEATHER,
            desired16DayInterval: INTERVAL_WEATHER_16DAY,
            isGraphUpdates: false,
            query: searchParams,
            isPremium,
            units,
          }),
        );

        if (shouldFetchConsistencyData) {
          dispatch(
            fetchGraphConsistencyForecast({
              id: spotId,
              days: numDaysToFetch,
              intervalHours: INTERVAL_WAVE_COUNT,
              desired16DayInterval: INTERVAL_WAVE_COUNT_16DAY,
              isGraphUpdates: false,
              query: searchParams,
            }),
          );
        }
      } else {
        dispatch({ type: RESET_GRAPH_CONSISTENCY_FORECAST });
        dispatch({ type: RESET_GRAPH_WEATHER_FORECAST });
      }

      if (shouldFetchSwellSpectraData && !skipSwellSpectraFetch) {
        if (spectraGraphOpen) {
          // This will handle when the spotid changes and when historic dates or states change
          fetchSwellSpectraData();
        } else if (!skipSwellSpectraFetch) {
          dispatch({ type: RESET_GRAPH_SWELL_SPECTRA_FORECAST });
        }
      }
    },
    // Ensure this dependency array is staying up to date but do not introduce checks for
    // fetchSwellSpectraData, spectraGraphOpen, or spectraDataAvailable ever. We do not want to trigger
    // updates when spectra related properties change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      INTERVAL_RATING_16DAY,
      INTERVAL_WAVE_16DAY,
      INTERVAL_WAVE_COUNT_16DAY,
      INTERVAL_WIND_16DAY,
      consistencyEnabled,
      dispatch,
      isHistoricView,
      isPremium,
      maxDaysToFetch,
      numDaysToFetch,
      router.query,
      searchParams,
      shouldFetchConsistencyData,
      spotId,
      subregionId,
      supportsInsights,
      units,
      validStartDate,
    ],
  );

  // This ensures that toggling the historic forecast off will only fetch again if it changes this value
  // to false
  useEffect(() => {
    if (!isHistoricView) {
      fetchAllGraphData();
    }

    // We do not want to update when fetchAllGraphData updates from toggling between the
    // forecast and table view, therefore we are excluding it here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHistoricView]);

  // This ensures we fetch our graph data when the startDate has updated by the user for our historic forecasts.
  // This should only fire on that event.
  useEffect(() => {
    if (validStartDate && isHistoricView) {
      fetchAllGraphData();
    }

    // We do not want to update when fetchAllGraphData updates from toggling between the
    // forecast and table view, therefore we are excluding it here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHistoricView, validStartDate]);

  useEffect(() => {
    // This will fire for the first page load and when the accordion opens and closes
    if (spectraGraphOpen && !graphData?.swellSpectra?.period) {
      fetchSwellSpectraData();
    }

    // This useEffect should only ever update when the graph open variable changes for swell spectra
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spectraGraphOpen]);

  useEffect(() => {
    const swellModelTimestamp = graphData?.swells?.runInitializationTimestamp;
    const spectraModelTimestamp = graphData?.swellSpectra?.runInitializationTimestamp;
    const skipSwellSpectraFetch = true;

    if (
      swellModelTimestamp &&
      spectraModelTimestamp &&
      swellModelTimestamp !== spectraModelTimestamp
    ) {
      // When our model run gets out of sync it will most likely be because we have fetched our swell spectra data after
      // we have fetched our intial wave data. If this is the case we want to fetch all of our graph data again but we do not want to fetch swell spectra data again.
      fetchAllGraphData(skipSwellSpectraFetch);
    }
    // This useEffect should only ever update when the graph open variable changes for swell spectra model timestamp run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphData.swellSpectra?.runInitializationTimestamp]);

  const didCoreFetchesError = Boolean(
    graphData.ratings.error &&
      graphData.sunlightTimes.error &&
      graphData.tide.error &&
      graphData.surf.error &&
      graphData.swells.error &&
      graphData.wind.error,
  );

  return {
    ...graphData,
    didCoreFetchesError,
    liveWind: {
      data: liveWindData?.data,
      units: liveWindData?.associated?.units,
      loading: liveWindDataLoading,
      error: liveWindDataError,
    },
  };
};

export default useFetchGraphData;
